'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { absurd } from '~/lib/handy-types';
import { useToggle } from '~/lib/use-toggle';
import { LoadingButton } from '~/components/ui-custom/loading-button';
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import { signInWithCredentialsInClientComponent } from '~/auth/auth-react-bindings/auth-react-bindings-client';
import { credentialsSignInSchema, type CredentialsSignInSchema } from '~/auth/auth-services/auth-credentials-schemas';
import { ErrorIcon } from '~/icons';
import { useRouter } from '~/i18n/navigation';
import { ResetPasswordPinDialog } from '../../../_components/reset-password-pin-dialog';
import { PasswordInput } from './password-input';
export const UserAuthForm = ({
  isOAuthNotLinkedError
}: {
  isOAuthNotLinkedError: boolean;
}) => {
  const t = useTranslations('UserAuthForm');
  const searchParams = useSearchParams();
  const router = useRouter();
  const resetPasswordDialog = useToggle(false);
  const $signInWithCredentialsInClientComponent = useMutation({
    mutationFn: signInWithCredentialsInClientComponent,
    onSuccess: result => {
      if (result.ok) {
        // Currently welcome page is only for Parent and Only parent roles can login with credentials, so it's safe
        router.push('/welcome');
      }
      if (!result.ok) {
        switch (result.errorCode) {
          case 'incorrectCredentials':
            {
              toast.error('Email address or password is incorrect.');
              break;
            }
          case 'userInactive':
            {
              toast.error('The user account is inactive.');
              break;
            }
          case 'userPending':
            {
              toast.error("This account hasn't been activated yet. Follow the link in the invitation email to set up the account.");
              break;
            }
          case 'passwordLoginNotAllowed':
            {
              toast.error('Password login is not allowed for this user.');
              break;
            }
          case 'serverError':
            {
              toast.error('Server error. Please try again later.');
              break;
            }
          default:
            {
              absurd(result.errorCode);
            }
        }
      }
    }
  });
  const form = useForm<CredentialsSignInSchema>({
    resolver: zodResolver(credentialsSignInSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  });
  return <>
      <Form {...form} data-sentry-element="Form" data-sentry-source-file="user-auth-form.tsx">
        <form autoComplete="off" className="flex w-full flex-col gap-y-6" onSubmit={form.handleSubmit(values => {
        $signInWithCredentialsInClientComponent.mutate({
          credentials: values,
          callbackUrl: searchParams.get('callbackUrl') ?? '/'
        });
      })}>
          <FormField name="email" control={form.control} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-base font-medium text-black">{t('email')}</FormLabel>
                <FormControl>
                  <Input type="email" {...field} autoComplete="off" autoFocus />
                </FormControl>
                <FormMessage />
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="user-auth-form.tsx" />
          <FormField name="password" control={form.control} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-base font-medium text-black">{t('password')}</FormLabel>
                <FormControl>
                  <PasswordInput {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="user-auth-form.tsx" />

          {isOAuthNotLinkedError && <Alert variant="destructive">
              <ErrorIcon className="size-4" />
              <AlertTitle>Cannot sign in with Google account</AlertTitle>
              <AlertDescription>
                To confirm your identity, sign in with the same account you used originally. You will be able
                to link your Google account in the profile settings after you sign in with email and password.
              </AlertDescription>
            </Alert>}

          <div className="text-center">
            <span className="mr-1 text-base text-[#7B7A7B] md:mr-0 lg:mr-2.5">{t('forgot_password')}</span>
            <Button type="button" variant="link" className="p-0 hover:no-underline" onClick={resetPasswordDialog.on} data-sentry-element="Button" data-sentry-source-file="user-auth-form.tsx">
              {t('reset_password')}
            </Button>
          </div>
          <LoadingButton type="submit" variant="default" size="sm" isLoading={$signInWithCredentialsInClientComponent.isPending} data-sentry-element="LoadingButton" data-sentry-source-file="user-auth-form.tsx">
            {t('sign_in')}
          </LoadingButton>
        </form>
      </Form>
      {resetPasswordDialog.isActive && <ResetPasswordPinDialog type="password" onClose={resetPasswordDialog.off} onSubmit={(_values, onSuccess) => onSuccess()} />}
    </>;
};